import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import {
  CustomButtonWithBorder,
  CustomButton,
} from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  nameLength,
  childBoxStyle,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import {
  specialCharacterRestrict,
  onlyNumberRegex,
  ifscCodePatterns,
} from "services/constants/patterns";

export const EditPaymentBank = ({
  onClose,
  individualBankData,
  reCallApi,
  setGetId,
  setIsEditPanelOpen,
  setHanldeLoader,
  setHasUnsavedChanges,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      bankName: "",
      nick_name: "",
      bankAccountNumber: "",
      confirm_account_number: "",
      branchName: "",
      ifscCode: "",
    },
  });
  const { domain } = useSelector((state) => state.tokenReducer);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const formValues = watch();
  const deepEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  useEffect(() => {
    if (individualBankData && !initialFormValues) {
      const defaultValues = {
        bankAccountNumber: individualBankData?.bank_account_number,
        bankName: individualBankData?.bank_name,
        branchName: individualBankData?.bank_branch,
        ifscCode: individualBankData?.bank_ifsc_code,
        confirm_account_number: individualBankData?.bank_account_number,
        nick_name: individualBankData?.nick_name,
      };
      setInitialFormValues(defaultValues);
      reset(defaultValues);
    }
  }, [individualBankData, initialFormValues, reset]);

  useEffect(() => {
    if (initialFormValues && formValues) {
      const hasChanges = !deepEqual(formValues, initialFormValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formValues, initialFormValues, setHasUnsavedChanges]);

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}payroll/setup/paymentbank/payment-bank/${individualBankData.id}`,
      "PUT",
      {
        bank_account_number: data?.bankAccountNumber,
        bank_name: data?.bankName,
        bank_branch: data?.branchName,
        bank_ifsc_code: data?.ifscCode,
        company: individualBankData?.company,
        nick_name: data?.nick_name,
      }
    );
    if (response.ok) {
      showToast("success", "Bank details updated.");
      setIsEditPanelOpen(false);
      setValue("button_loader", false);
      setGetId(individualBankData?.id);
      setHanldeLoader(true);
      reCallApi(true);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const updateBankName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= nameLength
    ) {
      setValue("bankName", event.target.value);
    } else {
      setValue("bankName", "");
    }
  };

  const updateNickName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 64
    ) {
      setValue("nick_name", event.target.value);
    }
  };

  const updateBankAccountNumber = (event) => {
    if (
      event.target.value.length <= 18 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setValue("bankAccountNumber", event.target.value);
    }
  };

  const updateConfirmAccountNumber = (event) => {
    if (
      event.target.value.length <= 18 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setValue("confirm_account_number", event.target.value);
    }
  };

  const updateBranchName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 32
    ) {
      setValue("branchName", event.target.value);
    }
  };

  const updateIFSCcode = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 11
    ) {
      setValue("ifscCode", event.target.value);
    }
  };

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit Payment Bank
            </Typography>
            <Typography {...infoStyle}>
              Edit your payment bank details.
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={childBoxStyle}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Company Name</Typography>
              <Typography {...infoStyle}>
                Select the company for which this payrun cycle appliess
              </Typography>
              <CustomTextBox
                selectedValue={individualBankData?.company_name}
                disabledValue={true}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Bank Name</Typography>
              <Typography {...infoStyle}>
                Update the official name of your bank if required
              </Typography>
              <CustomTextBox
                selectedValue={watch("bankName")}
                rules={{
                  ...register("bankName", {
                    required: "Bank name is required",
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateBankName}
                placeHolder="Enter Bank Name"
                errorMessage={
                  errors?.bankName?.message ? errors?.bankName?.message : ""
                }
                textBoxError={errors.bankName ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Nick Name</Typography>
              <Typography {...infoStyle}>
                Modify the short name used to identify this bank account
              </Typography>
              <CustomTextBox
                selectedValue={watch("nick_name")}
                rules={{
                  ...register("nick_name", {
                    required: "Nick name is required",
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateNickName}
                placeHolder="Enter Nick Name"
                errorMessage={
                  errors?.nick_name?.message ? errors?.nick_name?.message : ""
                }
                textBoxError={errors.nick_name ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Account Number</Typography>
              <Typography {...infoStyle}>
                Update the complete account number if it has changed
              </Typography>
              <CustomTextBox
                selectedValue={watch("bankAccountNumber")}
                rules={{
                  ...register("bankAccountNumber", {
                    required: "Bank account number is required",
                    minLength: {
                      value: 8,
                      message: "Minimum 8 characters required",
                    },
                  }),
                }}
                updateDataFunction={updateBankAccountNumber}
                placeHolder="Enter Bank Account Number"
                errorMessage={
                  errors?.bankAccountNumber?.message
                    ? errors?.bankAccountNumber?.message
                    : ""
                }
                textBoxError={errors.bankAccountNumber ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Confirm Account Number
              </Typography>
              <Typography {...infoStyle}>
                Re-enter the updated account number to confirm accuracy
              </Typography>
              <CustomTextBox
                selectedValue={watch("confirm_account_number")}
                rules={{
                  ...register("confirm_account_number", {
                    required: "Confirm account number is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value !== getValues("bankAccountNumber")) {
                          return "Account number and confirm account number should be same";
                        } else {
                          return true;
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateConfirmAccountNumber}
                placeHolder="Enter Confirm Account Number"
                errorMessage={
                  errors?.confirm_account_number?.message
                    ? errors?.confirm_account_number?.message
                    : ""
                }
                textBoxError={errors.confirm_account_number ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Branch</Typography>
              <Typography {...infoStyle}>
                Edit the bank branch name or location
              </Typography>
              <CustomTextBox
                selectedValue={watch("branchName")}
                rules={{
                  ...register("branchName", {
                    required: "Branch name is required",
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateBranchName}
                placeHolder="Enter Branch Name"
                errorMessage={
                  errors?.branchName?.message ? errors?.branchName?.message : ""
                }
                textBoxError={errors.branchName ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>IFSC Code</Typography>
              <Typography {...infoStyle}>
                Update the IFSC code for the selected bank branch
              </Typography>
              <CustomTextBox
                selectedValue={watch("ifscCode")}
                rules={{
                  ...register("ifscCode", {
                    required: "IFSC code is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 11 && value !== "") {
                          return "Minimum 11 characters with no spaces";
                        } else {
                          return true;
                        }
                      },
                      checkPatterns: (value) => {
                        if (!ifscCodePatterns.test(value)) {
                          return "Please enter a valid format: ABCD0123456";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateIFSCcode}
                placeHolder="Enter IFSC Code (ex: ABCD0123456)"
                errorMessage={
                  errors?.ifscCode?.message ? errors?.ifscCode?.message : ""
                }
                textBoxError={errors.ifscCode ? true : false}
              />
            </Box>
            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName="Update"
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
